import { CheckoutData, CheckoutResult, PaymentDetails } from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

export type OnlinePaymentWorkflowStatus = {
  status: string;
  output?: {
    status: string;
    paymentId?: number;
    paymentGuid?: string;
    paymentUrl?: string;
    error?: string;
    checkoutResult?: any;
  };
  error?: string;
};

export const onlineTransactionsAPI = {
  // result.url contains the url for qr code
  createOnlinePaymentTx: async (
    amount: number,
    checkout: CheckoutData,
    checkoutId: string,
    signal?: AbortSignal,
  ) => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.online.$post(
      {
        json: {
          amount,
          checkout,
          checkoutId,
        },
      },
      {
        init: { signal },
      },
    );
    if (resp.ok) {
      return await resp.json();
    } else {
      let message;
      if (resp.status === 422) {
        const json: any = await resp.json();
        message = json.message || json.error;
      }
      throw new Error(message);
    }
  },
  processOnlinePaymentTx: async (paymentId: number, signal?: AbortSignal) => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.online[
      ":payment_id"
    ].process.$post(
      {
        param: { payment_id: paymentId.toString() },
      },
      {
        init: { signal },
      },
    );
    if (resp.ok) {
      const json = await resp.json();
      return json;
    } else {
      throw new Error("unable to fetch status of checkout");
    }
  },
  checkOnlinePaymentResult: async (paymentId: number, signal?: AbortSignal) => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.online[":payment_id"].$get(
      {
        param: { payment_id: paymentId.toString() },
      },
      {
        init: { signal },
      },
    );
    if (resp.ok) {
      return resp.json();
    } else {
      throw new Error("failed to poll");
    }
  },
  getOnlinePaymentTxResult: async (
    paymentId: number,
  ): Promise<{ payment: PaymentDetails; result: CheckoutResult }> => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.online.result[":payment_id"].$get(
      {
        param: { payment_id: paymentId.toString() },
      },
    );
    if (resp.ok) {
      const json = await resp.json();
      // todo: remove this typecast
      return json as { payment: PaymentDetails; result: CheckoutResult };
    } else {
      throw new Error("unable to fetch status of checkout");
    }
  },
  // Workflow-based API methods
  createOnlinePaymentWorkflow: async (
    amount: number,
    checkoutId: string,
    checkout: CheckoutData,
  ): Promise<{ workflowId: string; checkoutId: string }> => {
    throw new Error();
  },
  getOnlinePaymentWorkflowStatus: async (
    workflowId: string,
  ): Promise<OnlinePaymentWorkflowStatus> => {
    throw new Error();
  },
  // Cloudflare Workflow based API methods (calls server-side routes)
  invokeOnlinePaymentService: async (
    amount: number,
    _checkoutId: string,
    checkout: CheckoutData,
  ): Promise<{ invocationId: string; checkoutId: string }> => {
    const client = getAPIClient();
    const checkoutId = crypto.randomUUID();
    const resp = await client.api.v2.payments.online.workflow.$post({
      json: {
        amount,
        checkout: { ...checkout, id: checkoutId },
        checkoutId,
      },
    });

    if (resp.ok) {
      return await resp.json();
    } else {
      let message;
      if (resp.status === 422) {
        const json: any = await resp.json();
        message = json.message || json.error;
      }
      throw new Error(message || "Failed to start online payment");
    }
  },

  getOnlinePaymentServiceStatus: async (
    invocationId: string,
    signal?: AbortSignal,
  ): Promise<{
    status: "pending" | "completed" | "error";
    output?: any;
    error?: string;
  }> => {
    const client = getAPIClient();
    const resp = await client.api.v2.payments.online.workflow[
      ":invocationId"
    ].$get(
      {
        param: { invocationId },
      },
      {
        init: { signal },
      },
    );

    if (resp.ok) {
      const result = await resp.json();
      return result as {
        status: "pending" | "completed" | "error";
        output?: any;
        error?: string;
      };
    } else {
      throw new Error("Failed to get online payment status");
    }
  },
};
